import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import api from '@/lib/api';
import { Tag, Plus, Copy, MoreHorizontal } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { toast } from 'sonner';

const emptyForm = { code: '', type: 'percentage', value: 0, min_order_amount: 0, max_uses: 0, per_customer_limit: 0, expiry_date: '', is_active: true };

export default function CouponListPage() {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ ...emptyForm });
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchCoupons = async () => {
    setLoading(true);
    try { const { data } = await api.get('/coupons'); setCoupons(data); } catch {}
    setLoading(false);
  };
  useEffect(() => { fetchCoupons(); }, []);

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const openCreate = () => { setForm({ ...emptyForm }); setEditId(null); setDialogOpen(true); };
  const openEdit = (c) => { setForm({ ...c }); setEditId(c.id || c.code); setDialogOpen(true); };

  const handleSave = async () => {
    if (!form.code.trim()) { toast.error('Code is required'); return; }
    setSaving(true);
    try {
      if (editId) { await api.put(`/coupons/${editId}`, form); toast.success('Coupon updated'); }
      else { await api.post('/coupons', form); toast.success('Coupon created'); }
      setDialogOpen(false);
      fetchCoupons();
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
    setSaving(false);
  };

  const toggleActive = async (coupon) => {
    const id = coupon.id || coupon.code;
    try {
      await api.put(`/coupons/${id}`, { is_active: !coupon.is_active });
      fetchCoupons();
    } catch { toast.error('Toggle failed'); }
  };

  const handleDelete = async (coupon) => {
    if (!window.confirm('Delete this coupon?')) return;
    const id = coupon.id || coupon.code;
    try { await api.delete(`/coupons/${id}`); toast.success('Deleted'); fetchCoupons(); }
    catch { toast.error('Delete failed'); }
  };

  const inputStyle = { borderColor: 'rgba(26,26,26,0.12)' };
  const labelStyle = { color: 'rgba(26,26,26,0.6)' };

  return (
    <motion.div data-testid="coupon-list-page" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-serif text-2xl tracking-tight" style={{ color: '#1A1A1A' }}>Coupons</h1>
          <p className="font-sans text-sm mt-1" style={{ color: 'rgba(26,26,26,0.5)' }}>{coupons.length} coupons</p>
        </div>
        <Button data-testid="add-coupon-btn" onClick={openCreate} className="font-sans text-sm gap-2 rounded-none" style={{ backgroundColor: '#E85D3A', color: '#fff' }}>
          <Plus size={15} /> New Coupon
        </Button>
      </div>

      <div className="rounded-lg border overflow-hidden" style={{ borderColor: 'rgba(26,26,26,0.08)', backgroundColor: '#FDFBF7' }}>
        <table className="w-full">
          <thead>
            <tr style={{ backgroundColor: 'rgba(26,26,26,0.02)' }}>
              {['Code', 'Type', 'Value', 'Min Order', 'Uses', 'Expiry', 'Active', ''].map(h => (
                <th key={h} className="text-left p-3 font-sans text-xs uppercase tracking-wider font-medium" style={{ color: 'rgba(26,26,26,0.45)' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="p-12 text-center font-sans text-sm" style={{ color: 'rgba(26,26,26,0.4)' }}>Loading...</td></tr>
            ) : coupons.length === 0 ? (
              <tr><td colSpan={8} className="p-12 text-center">
                <Tag size={32} strokeWidth={1} className="mx-auto mb-3" style={{ color: 'rgba(26,26,26,0.2)' }} />
                <p className="font-sans text-sm" style={{ color: 'rgba(26,26,26,0.4)' }}>No coupons yet</p>
              </td></tr>
            ) : coupons.map(c => (
              <tr key={c.id || c.code} className="border-t hover:bg-[rgba(26,26,26,0.01)]" style={{ borderColor: 'rgba(26,26,26,0.06)' }}>
                <td className="p-3">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm font-medium" style={{ color: '#1A1A1A' }}>{c.code}</span>
                    <button onClick={() => { navigator.clipboard.writeText(c.code); toast.success('Copied!'); }}
                      className="p-0.5 rounded hover:bg-[rgba(26,26,26,0.05)]">
                      <Copy size={12} style={{ color: 'rgba(26,26,26,0.3)' }} />
                    </button>
                  </div>
                </td>
                <td className="p-3"><Badge className="rounded-sm font-sans text-[10px] uppercase tracking-wider" style={{ backgroundColor: 'rgba(196,149,106,0.1)', color: '#C4956A', border: 'none' }}>{c.type}</Badge></td>
                <td className="p-3"><span className="font-sans text-sm">{c.type === 'percentage' ? `${c.value}%` : c.type === 'free_shipping' ? 'Free' : `$${c.value}`}</span></td>
                <td className="p-3"><span className="font-sans text-sm" style={{ color: 'rgba(26,26,26,0.6)' }}>{c.min_order_amount > 0 ? `$${c.min_order_amount}` : '—'}</span></td>
                <td className="p-3"><span className="font-sans text-sm" style={{ color: 'rgba(26,26,26,0.6)' }}>{c.used_count || 0}{c.max_uses > 0 ? `/${c.max_uses}` : ''}</span></td>
                <td className="p-3"><span className="font-sans text-sm" style={{ color: 'rgba(26,26,26,0.6)' }}>{c.expiry_date ? new Date(c.expiry_date).toLocaleDateString() : '—'}</span></td>
                <td className="p-3"><Switch data-testid={`toggle-${c.code}`} checked={c.is_active} onCheckedChange={() => toggleActive(c)} /></td>
                <td className="p-3">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild><button className="p-1 rounded hover:bg-[rgba(26,26,26,0.05)]"><MoreHorizontal size={16} style={{ color: 'rgba(26,26,26,0.4)' }} /></button></DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => openEdit(c)} className="font-sans text-sm">Edit</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleDelete(c)} className="font-sans text-sm text-red-600">Delete</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle className="font-serif">{editId ? 'Edit Coupon' : 'New Coupon'}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="font-sans text-xs mb-1 block" style={labelStyle}>Code</Label>
              <Input data-testid="coupon-code-input" value={form.code} onChange={e => set('code', e.target.value)}
                placeholder="e.g. SUMMER20" className="font-sans text-sm rounded-none uppercase" style={inputStyle} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="font-sans text-xs mb-1 block" style={labelStyle}>Type</Label>
                <Select value={form.type} onValueChange={v => set('type', v)}>
                  <SelectTrigger className="font-sans text-sm rounded-none" style={inputStyle}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">Percentage</SelectItem>
                    <SelectItem value="fixed">Fixed Amount</SelectItem>
                    <SelectItem value="free_shipping">Free Shipping</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="font-sans text-xs mb-1 block" style={labelStyle}>Value</Label>
                <Input type="number" step="0.01" value={form.value} onChange={e => set('value', parseFloat(e.target.value) || 0)}
                  className="font-sans text-sm rounded-none" style={inputStyle} disabled={form.type === 'free_shipping'} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="font-sans text-xs mb-1 block" style={labelStyle}>Min Order ($)</Label>
                <Input type="number" step="0.01" value={form.min_order_amount} onChange={e => set('min_order_amount', parseFloat(e.target.value) || 0)}
                  className="font-sans text-sm rounded-none" style={inputStyle} />
              </div>
              <div>
                <Label className="font-sans text-xs mb-1 block" style={labelStyle}>Max Uses (0=unlimited)</Label>
                <Input type="number" value={form.max_uses} onChange={e => set('max_uses', parseInt(e.target.value) || 0)}
                  className="font-sans text-sm rounded-none" style={inputStyle} />
              </div>
            </div>
            <div>
              <Label className="font-sans text-xs mb-1 block" style={labelStyle}>Expiry Date</Label>
              <Input type="datetime-local" value={form.expiry_date?.slice(0, 16) || ''} onChange={e => set('expiry_date', e.target.value ? new Date(e.target.value).toISOString() : '')}
                className="font-sans text-sm rounded-none" style={inputStyle} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-none font-sans text-sm">Cancel</Button>
            <Button data-testid="save-coupon-btn" onClick={handleSave} disabled={saving}
              className="rounded-none font-sans text-sm" style={{ backgroundColor: '#E85D3A', color: '#fff' }}>
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
}
